import React from 'react';
import StyledReactSelect from './StyledReactSelect';
import { Options } from 'react-select';

export interface EntitySelectProps<T> {
  data: readonly T[];
  defaultOption: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

interface EntitySelectState {
  selectedOption: SelectOption | null;
}

export type SelectOption = { value: string; label: React.ReactNode };

export function generateEntitySelect<T>(
  valueGetterFn: (entity: T) => string,
  labelGetterFn: (entity: T) => React.ReactNode,
  nullable: boolean,
  matchFrom: 'start' | 'any',
  /** This component won't change the selected value if defaultOption changes. Pass true here if you want defaultOption to act as a value also. */
  useDefaultAsValue = false,
) {
  class EntitySelect extends React.Component<EntitySelectProps<T>, EntitySelectState> {
    constructor(props: EntitySelectProps<T>) {
      super(props);
      const { defaultOption, data } = props;
      const defaultEntity = defaultOption ? data.find((entry) => valueGetterFn(entry) === defaultOption) : null;

      this.state = {
        selectedOption:
          defaultOption && defaultEntity
            ? {
                value: defaultOption,
                label: labelGetterFn(defaultEntity),
              }
            : null,
      };
    }

    componentDidUpdate(prevProps: EntitySelectProps<T>) {
      const { defaultOption, data } = this.props;
      if (useDefaultAsValue && defaultOption !== prevProps.defaultOption) {
        const defaultEntity = defaultOption ? data.find((entry) => valueGetterFn(entry) === defaultOption) : null;
        this.setState({
          selectedOption:
            defaultOption && defaultEntity
              ? {
                  value: defaultOption,
                  label: labelGetterFn(defaultEntity),
                }
              : null,
        });
      }
    }

    render() {
      const { data, disabled, placeholder } = this.props;
      const { selectedOption } = this.state;
      const options: SelectOption[] = data.map((entry) => ({
        value: valueGetterFn(entry),
        label: labelGetterFn(entry),
      }));

      return (
        <StyledReactSelect
          options={options}
          isDisabled={disabled}
          value={selectedOption}
          onChange={this.changeSelection}
          nullable={!!nullable}
          matchFrom={matchFrom}
          isMulti={false}
          placeholder={placeholder}
        />
      );
    }

    private changeSelection = (option: SelectOption | Options<SelectOption> | null) => {
      this.setState({ selectedOption: option as SelectOption });
      this.props.onChange(option ? (option as SelectOption).value : null);
    };
  }

  return EntitySelect;
}
